import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import {
  getFirestore,
  collection,
  query,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  getCountFromServer,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { InputField, Button } from "@cakrawalajakarta/shared-components";
import { motion } from "framer-motion";

export const RPCS = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [state, setState] = useState({
    filteredParticipants: [],
    searchQuery: "",
    loading: true,
    totalFiltered: 0,
    allParticipants: [],
    isAdminOrRpc: false,
    greeting: "",
    userName: "",
    showAll: false,
    locationCounts: {
      "Decathlon Agora Mall": { total: 0, collected: 0, notCollected: 0 },
      "Balai Besar TNGGP": { total: 0, collected: 0, notCollected: 0 },
      "Belum Submit": { total: 0, collected: 0, notCollected: 0 },
    },
  });
  const [expandedParticipantId, setExpandedParticipantId] = useState(null);
  const [delegateNames, setDelegateNames] = useState({});
  const [delegateSearchQueries, setDelegateSearchQueries] = useState({});
  const [sortOrder, setSortOrder] = useState(""); // "" (default), "asc", "desc"
  const db = getFirestore();
  const navigate = useNavigate();
  const isMounted = useRef(true);

  // Determine greeting based on time
  useEffect(() => {
    const hour = new Date().getHours();
    let greeting = "";
    if (hour < 12) greeting = "Selamat Pagi";
    else if (hour < 15) greeting = "Selamat Siang";
    else if (hour < 18) greeting = "Selamat Sore";
    else greeting = "Selamat Malam";
    setState((prev) => ({ ...prev, greeting }));
  }, []);

  // Get user name
  useEffect(() => {
    if (user && user.displayName) {
      setState((prev) => ({ ...prev, userName: user.displayName }));
    } else {
      setState((prev) => ({ ...prev, userName: "Petugas RPC" }));
    }
  }, [user]);

  // Check admin or rpc status
  useEffect(() => {
    if (!user) {
      showNotification(
        "error",
        "Mohon masuk sebagai admin atau petugas RPC terlebih dahulu."
      );
      navigate("/Login");
      return;
    }

    const checkAccess = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (
          userDoc.exists() &&
          (userDoc.data().role === "admin" || userDoc.data().role === "rpc")
        ) {
          setState((prev) => ({ ...prev, isAdminOrRpc: true }));
        } else {
          showNotification(
            "error",
            "Akses ditolak: Anda tidak memiliki izin admin atau RPC."
          );
          navigate("/");
        }
      } catch (err) {
        console.error("[ERROR] Checking access status:", err);
        showNotification(
          "error",
          "Gagal memeriksa status akses. Silakan coba lagi."
        );
        navigate("/Login");
      }
    };

    checkAccess();
  }, [user, db, navigate, showNotification]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const mapDocToParticipant = (doc) => {
    const data = doc.data();
    // Include main account as the first member for Mix Group (20K)
    const mainAccountMember =
      data.kategori === "Mix Group (20K)" && data.namaLengkap
        ? {
            namaDepan: data.namaLengkap.split(" ")[0] || "",
            namaBelakang:
              data.namaLengkap.split(" ").slice(1).join(" ") || "",
            gender: data.gender || "Tidak diketahui",
            bibNumber: data.bibNumber || data.bibName || "",
            sizeJersey: data.sizeJersey || "-",
          }
        : null;
    const participants = Array.isArray(data.participants)
      ? [
          mainAccountMember,
          ...data.participants.map((member) => ({
            namaDepan: member.namaDepan || "",
            namaBelakang: member.namaBelakang || "",
            gender: member.gender || "Tidak diketahui",
            bibNumber: member.bibNumber || member.bibName || "",
            sizeJersey: member.sizeJersey || "-",
          })),
        ].filter(Boolean)
      : mainAccountMember
      ? [mainAccountMember]
      : [];

    // Create bibNumber array for Mix Group
    const bibNumbers =
      data.kategori === "Mix Group (20K)" && participants.length > 0
        ? participants
            .map((member) => member.bibNumber)
            .filter((bib) => bib && bib !== "")
        : [data.bibNumber || data.bibName || "-"];

    // Create sizeJersey array for Mix Group
    const sizeJerseys =
      data.kategori === "Mix Group (20K)" && participants.length > 0
        ? participants
            .map((member) => member.sizeJersey)
            .filter((size) => size && size !== "-")
        : [data.sizeJersey || "-"];

    return {
      id: doc.id,
      nama:
        data.teamName && data.kategori === "Mix Group (20K)"
          ? data.teamName
          : data.namaLengkap || "Nama tidak tersedia",
      namaLengkap: data.namaLengkap || "Nama tidak tersedia",
      teamName: data.teamName || null,
      gender:
        data.kategori === "Mix Group (20K)"
          ? "Mix"
          : data.gender || "Tidak diketahui",
      kategori: data.kategori || "Tidak diketahui",
      bibNumber: bibNumbers,
      sizeJersey: sizeJerseys,
      rpcLocation: data.rpcLocation || "Belum Submit",
      racePackCollected: data.racePackCollected || false,
      rpcDelegateName: data.rpcDelegateName || "",
      email: data.email || "",
      telepon: data.telepon || "",
      nomorIdentitas: data.nomorIdentitas || "",
      orderId: data.orderId || "-",
      transaction_status: data.transaction_status || "-",
      participants,
    };
  };

  // Fetch all participants stats and restricted list
  const fetchAllParticipants = useCallback(async () => {
    try {
      if (!isMounted.current) return;
      setState((prev) => ({ ...prev, loading: true }));

      const participantsCollection = collection(db, "pendaftaranJOTR");
      
      // Calculate location counts via server-side aggregation for efficiency
      const locations = ["Decathlon Agora Mall", "Balai Besar TNGGP", "Belum Submit"];
      const counts = {};
      
      await Promise.all(locations.map(async (loc) => {
          const baseQ = query(participantsCollection, 
              where("transaction_status", "in", ["settlement", "capture"]),
              where("rpcLocation", "==", loc)
          );
          
          const totalSnap = await getCountFromServer(baseQ);
          const collectedSnap = await getCountFromServer(query(baseQ, where("racePackCollected", "==", true)));
          
          const total = totalSnap.data().count;
          const collected = collectedSnap.data().count;
          
          counts[loc] = {
              total,
              collected,
              notCollected: total - collected
          };
      }));

      // Fetch a limited initial list (e.g. 100) to keep initial load light
      // Search will trigger full list matching if needed, but better to keep it limited
      const q = query(
        participantsCollection,
        where("transaction_status", "in", ["settlement", "capture"]),
        orderBy("namaLengkap"),
        limit(100)
      );

      const snapshot = await getDocs(q);
      const initialParticipants = snapshot.docs.map(doc => {
          const data = doc.data();
          // ... (mapping remains same for these docs)
          return mapDocToParticipant(doc);
      });

      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          allParticipants: initialParticipants, // Note: only initial 100
          filteredParticipants: [],
          totalFiltered: 0,
          locationCounts: counts,
          loading: false,
        }));
      }
    } catch (err) {
      console.error("[ERROR] Fetching participants stats:", err);
      if (isMounted.current) {
        setState((prev) => ({ ...prev, loading: false }));
      }
    }
  }, [db]);

  // Handle search and sorting
  useEffect(() => {
    let filtered = [...state.allParticipants];

    // Apply search filter
    if (state.searchQuery && state.isAdminOrRpc) {
      const lowerQuery = state.searchQuery.toLowerCase().trim();
      filtered = state.allParticipants.filter((p) => {
        // Search main fields
        const mainFields = [
          p.namaLengkap,
          p.teamName,
          p.email,
          p.orderId,
          ...(Array.isArray(p.bibNumber) ? p.bibNumber : [p.bibNumber]),
        ].filter(Boolean);
        if (
          mainFields.some((field) => field.toLowerCase().includes(lowerQuery))
        ) {
          return true;
        }
        // Search group member names and bib numbers for Mix Group
        if (p.kategori === "Mix Group (20K)" && p.participants?.length > 0) {
          return p.participants.some((member) => {
            const fullName =
              `${member.namaDepan} ${member.namaBelakang}`.trim();
            return (
              fullName.toLowerCase().includes(lowerQuery) ||
              (member.bibNumber &&
                member.bibNumber.toLowerCase().includes(lowerQuery))
            );
          });
        }
        return false;
      });
    } else if (!state.showAll && !state.searchQuery) {
      filtered = [];
    }

    // Apply sorting by rpcLocation
    if (sortOrder) {
      filtered.sort((a, b) => {
        const locA = a.rpcLocation || "";
        const locB = b.rpcLocation || "";
        if (sortOrder === "asc") {
          return locA.localeCompare(locB);
        } else {
          return locB.localeCompare(locB);
        }
      });
    }

    if (isMounted.current) {
      setState((prev) => ({
        ...prev,
        filteredParticipants: filtered,
        totalFiltered: filtered.length,
      }));
    }
  }, [
    state.searchQuery,
    state.allParticipants,
    state.isAdminOrRpc,
    state.showAll,
    sortOrder,
  ]);

  // Initial data fetch
  useEffect(() => {
    if (state.isAdminOrRpc) {
      fetchAllParticipants();
    }
  }, [fetchAllParticipants, state.isAdminOrRpc]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setState((prev) => ({
      ...prev,
      searchQuery: e.target.value,
      showAll: false,
    }));
    setExpandedParticipantId(null);
  };

  // Handle show all participants toggle
  const handleToggleShowAll = () => {
    setState((prev) => ({ ...prev, showAll: !prev.showAll, searchQuery: "" }));
    setExpandedParticipantId(null);
  };

  // Handle sort by location
  const handleSortByLocation = () => {
    setSortOrder((prev) => {
      if (prev === "") return "asc";
      if (prev === "asc") return "desc";
      return "";
    });
    setExpandedParticipantId(null);
  };

  // Handle delegate name change (manual input)
  const handleDelegateNameChange = (id, value) => {
    setDelegateNames((prev) => ({ ...prev, [id]: value }));
    setDelegateSearchQueries((prev) => ({ ...prev, [id]: "" }));
  };

  // Handle delegate search query change
  const handleDelegateSearchChange = (id, value) => {
    setDelegateSearchQueries((prev) => ({ ...prev, [id]: value }));

    // Find matching participant based on input
    const matchedParticipant = state.allParticipants.find((p) =>
      p.namaLengkap.toLowerCase().startsWith(value.toLowerCase())
    );

    if (
      matchedParticipant &&
      value &&
      matchedParticipant.namaLengkap
        .toLowerCase()
        .startsWith(value.toLowerCase())
    ) {
      setDelegateNames((prev) => ({
        ...prev,
        [id]: matchedParticipant.namaLengkap,
      }));
    } else {
      setDelegateNames((prev) => ({ ...prev, [id]: value }));
    }
  };

  // Toggle racePackCollected status
  const handleToggleRacePack = async (participant) => {
    const newStatus = !participant.racePackCollected;
    const delegateName = delegateNames[participant.id] || "";
    try {
      const participantRef = doc(db, "pendaftaranJOTR", participant.id);
      await updateDoc(participantRef, {
        racePackCollected: newStatus,
        rpcDelegateName: newStatus ? delegateName : "",
        updatedAt: new Date(),
      });
      setState((prev) => ({
        ...prev,
        allParticipants: prev.allParticipants.map((p) =>
          p.id === participant.id
            ? {
                ...p,
                racePackCollected: newStatus,
                rpcDelegateName: newStatus ? delegateName : "",
              }
            : p
        ),
        filteredParticipants: prev.filteredParticipants.map((p) =>
          p.id === participant.id
            ? {
                ...p,
                racePackCollected: newStatus,
                rpcDelegateName: newStatus ? delegateName : "",
              }
            : p
        ),
      }));
      if (!newStatus) {
        setDelegateNames((prev) => ({ ...prev, [participant.id]: "" }));
        setDelegateSearchQueries((prev) => ({ ...prev, [participant.id]: "" }));
      }
      showNotification(
        "success",
        `Race pack untuk ${participant.nama} berhasil ditandai sebagai ${
          newStatus ? "diambil" : "belum diambil"
        }.`
      );
      fetchAllParticipants();
    } catch (err) {
      console.error("[ERROR] Updating racePackCollected:", err);
      showNotification(
        "error",
        "Gagal memperbarui status race pack: " + err.message
      );
    }
  };

  // Handle expand participant
  const handleExpand = (id) => {
    setExpandedParticipantId((prevId) => (prevId === id ? null : id));
  };

  // Convert phone number to international format for WhatsApp
  const formatWhatsAppNumber = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      return `+62${cleaned.slice(1)}`;
    }
    return cleaned;
  };

  const rangeText = `${state.filteredParticipants.length} dari ${state.totalFiltered} Peserta`;

  if (!state.isAdminOrRpc) return null;

  return (
    <section
      id="rpc-system"
      className="w-full min-h-screen py-20 text-lime-600 flex-col justify-center relative bg-white"
    >
      <div className="relative z-0 container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Page Title */}
          <div className="text-center mb-8">
            <motion.h2
              className="text-2xl font-black uppercase tracking-wider text-lime-600"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              RACE PACK COLLECTION
            </motion.h2>
            <motion.h3
              className="text-3xl md:text-5xl font-black text-lime-600 mt-2"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              JOTR GEPANG SERIES
            </motion.h3>
          </div>

          {/* Location Counts Container */}
          <motion.div
            className="bg-white rounded-2xl border border-gray-200 p-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600 text-center">
              <div>
                <p className="text-lg font-bold text-lime-600">
                  Decathlon Agora Mall:{" "}
                  {state.locationCounts["Decathlon Agora Mall"].total} peserta
                </p>
                <p>
                  Sudah Diambil:{" "}
                  {state.locationCounts["Decathlon Agora Mall"].collected}{" "}
                  peserta
                </p>
                <p>
                  Belum Diambil:{" "}
                  {state.locationCounts["Decathlon Agora Mall"].notCollected}{" "}
                  peserta
                </p>
              </div>
              <div>
                <p className="text-lg font-bold text-lime-600">
                  Belum Submit: {state.locationCounts["Belum Submit"].total}{" "}
                  peserta
                </p>
                <p>
                  Sudah Diambil:{" "}
                  {state.locationCounts["Belum Submit"].collected} peserta
                </p>
                <p>
                  Belum Diambil:{" "}
                  {state.locationCounts["Belum Submit"].notCollected} peserta
                </p>
              </div>
              <div>
                <p className="text-lg font-bold text-lime-600">
                  Balai Besar TNGGP:{" "}
                  {state.locationCounts["Balai Besar TNGGP"].total} peserta
                </p>
                <p>
                  Sudah Diambil:{" "}
                  {state.locationCounts["Balai Besar TNGGP"].collected} peserta
                </p>
                <p>
                  Belum Diambil:{" "}
                  {state.locationCounts["Balai Besar TNGGP"].notCollected}{" "}
                  peserta
                </p>
              </div>
            </div>
          </motion.div>

          {/* Greeting and Search Section */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
              <p className="text-base text-gray-600">{state.greeting}</p>
              <p className="text-2xl font-bold text-lime-600 uppercase mt-2">
                {state.userName}
              </p>
              <p className="text-sm bg-lime-50 border border-lime-400 rounded-xl text-lime-600 mt-2 px-4 py-1 inline-block">
                JOTR Racepack Collection Crew
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
              <p className="text-base text-gray-600 font-medium mb-2">
                Cari Peserta
              </p>
              <div className="w-full max-w-xs mx-auto">
                <InputField
                  type="text"
                  placeholder="Cari Peserta (Nama / No. Invoice)"
                  value={state.searchQuery}
                  onChange={handleSearchChange}
                  startIcon={
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="text-gray-400"
                    />
                  }
                />
              </div>
              <Button
                variant="ghost"
                onClick={handleToggleShowAll}
                className="text-lime-600 hover:underline text-base mt-2 h-auto px-0"
              >
                {state.showAll
                  ? "Sembunyikan Peserta"
                  : "Tampilkan Semua Peserta"}
              </Button>
            </div>
          </motion.div>

          {/* Search Results Section */}
          <motion.div
            className="bg-white rounded-2xl border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <div className="space-y-0">
              {state.loading ? (
                <div className="text-left text-base text-gray-600 p-4">
                  Memuat data...
                </div>
              ) : !state.searchQuery && !state.showAll ? (
                <div className="text-center text-base text-gray-600 p-4">
                  Silakan masukkan nama peserta, nama grup, atau BIB number
                  untuk mencari atau klik "Tampilkan Semua Peserta".
                </div>
              ) : state.filteredParticipants.length === 0 ? (
                <div className="text-left text-base text-gray-600 p-4">
                  Tidak ada peserta yang cocok dengan pencarian.
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-6 gap-2 items-center px-4 py-4 bg-lime-50 rounded-t-2xl text-lime-600 font-semibold text-sm">
                    <span className="text-left">Nama</span>
                    <span className="text-left">Gender</span>
                    <span className="text-left">Kategori</span>
                    <span className="text-left">BIB Number</span>
                    <span
                      className="text-left cursor-pointer hover:underline"
                      onClick={handleSortByLocation}
                    >
                      RPC Lokasi{" "}
                      {sortOrder === "asc" && (
                        <FontAwesomeIcon icon={faChevronUp} />
                      )}
                      {sortOrder === "desc" && (
                        <FontAwesomeIcon icon={faChevronDown} />
                      )}
                    </span>
                    <span className="text-left">Status</span>
                  </div>
                  {state.filteredParticipants.map((p) => (
                    <motion.div
                      key={p.id}
                      className="overflow-hidden bg-white"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <button
                        className="w-full grid grid-cols-6 gap-2 items-center px-4 py-4 text-sm border-t border-gray-200 hover:bg-lime-50"
                        onClick={() => handleExpand(p.id)}
                      >
                        <span className="text-gray-800 text-left truncate">
                          {p.nama ? p.nama.toUpperCase() : "-"}
                        </span>
                        <span className="text-gray-500 text-left">
                          {p.gender || "-"}
                        </span>
                        <span className="text-gray-500 text-left truncate">
                          {p.kategori || "-"}
                        </span>
                        <span className="text-gray-500 text-left font-medium">
                          {p.kategori === "Mix Group (20K)" ? (
                            p.bibNumber.length > 0 ? (
                              p.bibNumber.map((bib, index) => (
                                <span
                                  key={index}
                                  className="bg-lime-100 text-lime-800 px-2 py-1 rounded text-xs mr-1"
                                >
                                  {bib}
                                </span>
                              ))
                            ) : (
                              "-"
                            )
                          ) : p.bibNumber[0] ? (
                            <span className="bg-lime-100 text-lime-800 px-2 py-1 rounded text-xs">
                              {p.bibNumber[0]}
                            </span>
                          ) : (
                            "-"
                          )}
                        </span>
                        <span className="text-gray-500 text-left truncate">
                          {p.rpcLocation || "-"}
                        </span>
                        <div className="flex justify-start items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded-lg text-sm ${
                              p.racePackCollected
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {p.racePackCollected ? "Diambil" : "Belum Diambil"}
                          </span>
                          <FontAwesomeIcon
                            icon={
                              expandedParticipantId === p.id
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="text-gray-500"
                          />
                        </div>
                      </button>
                      {expandedParticipantId === p.id && (
                        <div className=" py-4 text-base text-gray-600 border border-gray-200 rounded-2xl mx-4 my-4 space-y-4">
                          {p.kategori === "Mix Group (20K)" &&
                            p.participants?.length > 0 && (
                              <div>
                                <h4 className="text-lg px-4 font-semibold text-lime-600 mb-2">
                                  Anggota Grup: {p.nama}
                                </h4>
                                <table className="w-full table-auto border-none">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                                        Nama
                                      </th>
                                      <th className="py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                                        Gender
                                      </th>
                                      <th className="py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                                        BIB Number
                                      </th>
                                      <th className="py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                                        Size Jersey
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {p.participants.map((member, index) => (
                                      <tr
                                        key={`${member.namaDepan}-${member.namaBelakang}-${index}`}
                                        className={
                                          index === p.participants.length - 1
                                            ? ""
                                            : "border-b border-gray-200"
                                        }
                                      >
                                        <td className="px-4 py-2 text-sm text-gray-600 text-center">
                                          {`${member.namaDepan || ""} ${
                                            member.namaBelakang || ""
                                          }`.trim() || "Nama tidak tersedia"}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600 text-center">
                                          {member.gender || "Tidak diketahui"}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600 text-center">
                                          {member.bibNumber || "-"}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600 text-center">
                                          {member.sizeJersey || "-"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          <div className="grid grid-cols-1 sm:grid-cols-3 px-4 gap-4">
                            <div className="h-36 text-center">
                              <div className="bg-lime-50 border border-lime-400 h-full px-4 py-2 rounded-xl flex flex-col items-center justify-between">
                                <div className="flex-grow flex items-center justify-center">
                                  <p className="text-2xl font-bold text-lime-600">
                                    {p.kategori === "Mix Group (20K)"
                                      ? p.bibNumber.join(", ")
                                      : p.bibNumber[0] || "-"}
                                  </p>
                                </div>
                                <span className="text-base text-gray-700">
                                  BIB Number
                                </span>
                              </div>
                            </div>
                            <div className="h-36 text-center">
                              <div className="bg-lime-50 border border-lime-400 h-full px-4 py-2 rounded-xl flex flex-col items-center justify-between">
                                <div className="flex-grow flex items-center justify-center">
                                  <p className="text-2xl font-bold text-lime-600">
                                    {p.kategori || "-"}
                                  </p>
                                </div>
                                <span className="text-base text-gray-700">
                                  Kategori
                                </span>
                              </div>
                            </div>
                            <div className="h-36 text-center">
                              <div className="bg-lime-50 border border-lime-400 h-full px-4 py-2 rounded-xl flex flex-col items-center justify-between">
                                <div className="flex-grow flex items-center justify-center">
                                  <p className="text-2xl font-bold text-lime-600">
                                    {p.kategori === "Mix Group (20K)"
                                      ? p.sizeJersey.join(", ")
                                      : p.sizeJersey[0] || "-"}
                                  </p>
                                </div>
                                <span className="text-base text-gray-700">
                                  Size Jersey
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 px-4 sm:grid-cols-3 gap-x-4 gap-y-2">
                            <p className="text-left">
                              <span className="text-gray-700">Nama:</span>{" "}
                              {p.nama || "-"}
                            </p>
                            <p className="text-left">{p.orderId || "-"}</p>
                            <p className="text-left">
                              <span className="text-gray-700">Email:</span>{" "}
                              {p.email ? (
                                <a
                                  href={`mailto:${p.email}`}
                                  className="text-lime-600 hover:underline"
                                >
                                  {p.email}
                                </a>
                              ) : (
                                "-"
                              )}
                            </p>
                            <p className="text-left">
                              <span className="text-gray-700">
                                Nomor Telepon:
                              </span>{" "}
                              {p.telepon ? (
                                <a
                                  href={`https://wa.me/${formatWhatsAppNumber(
                                    p.telepon
                                  )}`}
                                  className="text-lime-600 hover:underline"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {p.telepon}
                                </a>
                              ) : (
                                "-"
                              )}
                            </p>
                            <p className="text-left">
                              <span className="text-gray-700">
                                Nomor Identitas:
                              </span>{" "}
                              {p.nomorIdentitas || "-"}
                            </p>
                            <p className="text-left">
                              <span className="text-gray-700">Lokasi RPC:</span>{" "}
                              {p.rpcLocation || "-"}
                            </p>
                            <p className="text-left">
                              <span className="text-gray-700">
                                Status Pembayaran:
                              </span>{" "}
                              {p.transaction_status || "-"}
                            </p>
                            <p className="text-left">
                              <span className="text-gray-700">
                                Nama Wakil (jika diwakilkan):
                              </span>{" "}
                              {p.rpcDelegateName || "-"}
                            </p>
                          </div>
                          {!p.racePackCollected && (
                            <div className="grid grid-cols-1 px-4 sm:grid-cols-2 gap-4">
                              <InputField
                                label="Nama Wakil (Umum)"
                                name={`delegate_manual_${p.id}`}
                                value={delegateNames[p.id] || ""}
                                onChange={(e) =>
                                  handleDelegateNameChange(p.id, e.target.value)
                                }
                                placeholder="Masukkan nama wakil"
                                inputClassName="w-full text-sm"
                                labelColor="text-gray-600"
                              />
                              <InputField
                                label="Nama Wakil (Jika wakil adalah peserta)"
                                name={`delegate_search_${p.id}`}
                                value={delegateSearchQueries[p.id] || ""}
                                onChange={(e) =>
                                  handleDelegateSearchChange(
                                    p.id,
                                    e.target.value
                                  )
                                }
                                placeholder="Cari nama peserta..."
                                inputClassName="w-full text-sm"
                                labelColor="text-gray-600"
                              />
                            </div>
                          )}
                          <div className="flex justify-center">
                            <Button
                              onClick={() => handleToggleRacePack(p)}
                              variant={p.racePackCollected ? "danger" : "success"}
                              className="w-full max-w-xs"
                              disabled={state.loading}
                            >
                              {p.racePackCollected
                                ? "Tandai Belum Diambil"
                                : "Tandai Diambil"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
            {state.filteredParticipants.length > 0 && (
              <div className="flex justify-end items-center px-4 py-4">
                <span className="text-base text-gray-600">{rangeText}</span>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
